import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { colors } from "utils";
import { logLiveKit, LogLevel } from "utils/sentryLogger";
import { useSessionContext } from "hooks/useSession";
import { useAppStateContext } from "hooks/useAppState/useAppState";
import { useAuthUserDataContext } from "hooks/useAuthUserData/useAuthUserData";
import okoCharacter from "assets/OKO_op2_yellow.svg";
import okoCharacterShadow from "assets/Shadow.svg";
import paintFrameImg from "assets/paint-frame.png";
import paintArrowImg from "assets/paint-arrow.png";

export const NoDeviceFound = ({ onDemoJoin, retryTrackAcquisition, isSessionReady, isRecordSessions }) => {
  const [showGuide, setShowGuide] = useState(true);
  const [permissionState, setPermissionState] = useState(null);
  const { session } = useSessionContext();
  const { groupCode } = useAppStateContext();
  const { authUserData } = useAuthUserDataContext();

  const isDemoMode = new URLSearchParams(window.location.search).get("demo") === "true";

  useEffect(() => {
    let micPermissionStatus;
    let camPermissionStatus;
    const onPermissionChange = () => {
      const micState = micPermissionStatus?.state;
      const camState = camPermissionStatus?.state;

      if (isRecordSessions) {
        // Both must be granted for recording sessions
        if (micState === 'granted' && camState === 'granted') {
          retryTrackAcquisition();
        } else if (micState === 'denied' || camState === 'denied') {
          setPermissionState('denied');
        }
      } else {
        setPermissionState(micState);
        if (micState === 'granted') {
          retryTrackAcquisition();
        }
      }
    };

    const promptAccess = async () => {
      try {
        const micStatus = await navigator.permissions.query({ name: 'microphone' });
        micPermissionStatus = micStatus;
        setPermissionState(micStatus.state);
        micStatus.addEventListener('change', onPermissionChange);

        let needsPrompt = micStatus.state === 'prompt';

        if (isRecordSessions) {
          try {
            const camStatus = await navigator.permissions.query({ name: 'camera' });
            camPermissionStatus = camStatus;
            camStatus.addEventListener('change', onPermissionChange);
            if (camStatus.state === 'denied') {
              setPermissionState('denied');
            }
            needsPrompt = needsPrompt || camStatus.state === 'prompt';
          } catch (e) {
            // camera permission query not supported — fall through
            logLiveKit("Camera permission query not supported", {
              level: LogLevel.WARNING,
              event: "camera_permission_query_unsupported",
              sessionId: session?.id,
              userId: authUserData?.id,
              groupCode,
              isRecordSessions,
              errorName: e?.name,
              errorMessage: e?.message,
            });
          }
        }

        if (needsPrompt) {
          const stream = await navigator.mediaDevices.getUserMedia({
            audio: true,
            ...(isRecordSessions && { video: true }),
          });
          stream.getTracks().forEach(track => track.stop());
          retryTrackAcquisition();
        }
      } catch (e) {
        // Permissions API not supported or getUserMedia failed — show manual guide
        logLiveKit("Permissions API or getUserMedia failed", {
          level: LogLevel.WARNING,
          event: "permission_or_getusermedia_failed",
          sessionId: session?.id,
          userId: authUserData?.id,
          groupCode,
          isRecordSessions,
          errorName: e?.name,
          errorMessage: e?.message,
        });
        setPermissionState('denied');
      }
    };

    promptAccess();
    return () => {
      micPermissionStatus?.removeEventListener('change', onPermissionChange);
      camPermissionStatus?.removeEventListener('change', onPermissionChange);
    };
  }, [retryTrackAcquisition, isRecordSessions]);

  const showManualGuide = showGuide && permissionState === 'denied';

  return (
    <NoDeviceContainer>
      {showManualGuide && (
        <>
          <PermissionOverlay />
          <PermissionGuide>
            <PaintArrow src={paintArrowImg} alt="" />
            <PaintFrame>
              <PaintFrameImg src={paintFrameImg} alt="" />
              <PaintFrameContent $compact={isRecordSessions}>
                <CloseButton onClick={() => setShowGuide(false)} aria-label="Close guide">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2.05155 15.648C1.58223 16.1173 0.82131 16.1173 0.35199 15.648V15.648C-0.11733 15.1787 -0.11733 14.4178 0.35199 13.9485L5.29897 9.00147C5.85207 8.44837 5.85207 7.55162 5.29897 6.99853L0.35199 2.05155C-0.11733 1.58223 -0.11733 0.82131 0.35199 0.35199V0.35199C0.82131 -0.11733 1.58223 -0.11733 2.05155 0.35199L6.99852 5.29897C7.55162 5.85207 8.44837 5.85207 9.00147 5.29897L13.9485 0.35199C14.4178 -0.11733 15.1787 -0.11733 15.648 0.35199V0.35199C16.1173 0.82131 16.1173 1.58223 15.648 2.05155L10.701 6.99852C10.1479 7.55162 10.1479 8.44837 10.701 9.00147L15.648 13.9485C16.1173 14.4178 16.1173 15.1787 15.648 15.648V15.648C15.1787 16.1173 14.4178 16.1173 13.9485 15.648L9.00147 10.701C8.44837 10.1479 7.55162 10.1479 6.99853 10.701L2.05155 15.648Z" fill="#FE1E7E"/>
                  </svg>
                </CloseButton>
                <GuideTitle $compact={isRecordSessions}>We can't hear you yet!</GuideTitle>
                <GuideSteps $compact={isRecordSessions}>
                  <GuideStep $compact={isRecordSessions}>1. Click the settings icon to the left of the web address</GuideStep>
                  <GuideStep $compact={isRecordSessions}>2. Switch the Microphone toggle to the right. It should turn blue!</GuideStep>
                  {isRecordSessions && (
                    <GuideStep $compact>3. Make sure that the Camera toggle is to the right and blue!</GuideStep>
                  )}
                </GuideSteps>
              </PaintFrameContent>
            </PaintFrame>
          </PermissionGuide>
        </>
      )}
      <NoDeviceTopSection>
        <NoDeviceTitle>
          {isRecordSessions
            ? "No Device Found! To continue OKO needs permission to use your microphone and camera. To enable see instructions above."
            : "No Audio Device Found! To continue OKO needs permission to use your microphone. To enable see instructions above."}
        </NoDeviceTitle>
        {isDemoMode ? (
          <DemoJoinButton onClick={onDemoJoin} disabled={!isSessionReady}>
            {isSessionReady ? "Join" : <Spinner />}
          </DemoJoinButton>
        ) : (
          <DisabledJoinButton disabled>Join</DisabledJoinButton>
        )}
      </NoDeviceTopSection>
      <NoDeviceBotWrapper>
        <NoDeviceBotShadow src={okoCharacterShadow} alt="" />
        <NoDeviceBot src={okoCharacter} alt="OKO" />
      </NoDeviceBotWrapper>
    </NoDeviceContainer>
  );
};

const NoDeviceContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  align-self: center;
  max-width: 1024px;
  max-height: 488px;
  width: 100%;

  @media (max-width: 1024px), (max-height: 700px) {
    max-width: 800px;
  }

  @media (max-height: 700px) {
    max-height: 360px;
  }
`;

const NoDeviceTopSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const NoDeviceTitle = styled.h2`
  color: #000;
  font-family: 'ABC Marfa', sans-serif;
  font-size: 24px;
  font-weight: 600;
  line-height: 32px;
  margin: 0;
  max-width: 644px;
`;

const NoDeviceBotWrapper = styled.div`
  position: relative;
`;

const NoDeviceBot = styled.img`
  width: 189px;
  display: block;
  position: relative;
  z-index: 2;

  @media (max-width: 1024px), (max-height: 700px) {
    width: 132px;
  }
`;

const NoDeviceBotShadow = styled.img`
  width: 189px;
  position: absolute;
  bottom: -10px;
  left: 0;
  z-index: 1;

  @media (max-width: 1024px), (max-height: 700px) {
    width: 132px;
  }
`;

const PermissionOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  opacity: 0.9;
  background: #FAF7F2;
  z-index: 1001;
`;

const PermissionGuide = styled.div`
  position: fixed;
  top: 60px;
  left: min(125px, 12vw);
  z-index: 1002;
  width: 380px;
  display: flex;
  align-items: flex-start;
`;

const PaintArrow = styled.img`
  width: 70px;
  height: auto;
  margin-top: -50px;
  margin-right: -10px;
  flex-shrink: 0;
`;

const PaintFrame = styled.div`
  position: relative;
  flex: 1;
`;

const PaintFrameImg = styled.img`
  width: 100%;
  height: auto;
  display: block;
`;

const PaintFrameContent = styled.div`
  position: absolute;
  inset: 20px 28px;
  display: flex;
  flex-direction: column;
  gap: ${({ $compact }) => ($compact ? '6px' : '12px')};
  justify-content: center;
`;

const CloseButton = styled.button`
  position: absolute;
  top: -6px;
  right: -8px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
`;

const GuideTitle = styled.h3`
  font-family: 'ABC Marfa', sans-serif;
  font-size: ${({ $compact }) => ($compact ? '1rem' : '1.15rem')};
  font-weight: 700;
  color: ${colors.pink500};
  margin: 0;
`;

const GuideSteps = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ $compact }) => ($compact ? '4px' : '8px')};
`;

const GuideStep = styled.p`
  font-size: ${({ $compact }) => ($compact ? '0.875rem' : '1rem')};
  font-weight: 500;
  line-height: ${({ $compact }) => ($compact ? '1.3' : '1.4')};
  color: ${colors.pink500};
  margin: 0;
`;

const DisabledJoinButton = styled.button`
  background-color: ${colors.pink500};
  border: none;
  border-radius: 20px;
  color: white;
  font-size: 16px;
  font-weight: 600;
  height: 48px;
  width: 205px;
  opacity: 0.5;
  cursor: not-allowed;
`;

const DemoJoinButton = styled.button`
  background-color: ${colors.pink500};
  border: none;
  border-radius: 20px;
  color: white;
  font-size: 16px;
  font-weight: 600;
  height: 48px;
  width: 205px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:disabled {
    opacity: 0.7;
    cursor: wait;
  }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const Spinner = styled.div`
  width: 20px;
  height: 20px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: ${spin} 0.6s linear infinite;
`;
